import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { AlertTriangle, Bug, CheckCircle2, Clock, Copy, FileWarning, Flag, Gauge, GitBranch, ListChecks, Package, Play, RefreshCw, Search, Shield, Terminal, Upload, Wrench } from "lucide-react";
import "./styles.css";

const SITE: {
  project: string;
  kind: string;
  name: string;
  short: string;
  title: string;
  description: string;
  category: string;
  accent: string;
  accent2: string;
  bg: string;
  panel: string;
  text: string;
  soft: string;
  shell: string;
  sample: string;
} = {
  "project": "renovaterisk-scout",
  "kind": "renovate",
  "name": "RenovateRisk Scout",
  "short": "RS",
  "title": "Renovate Config Risk Auditor - Automerge, minimumReleaseAge and hostRules Checker",
  "description": "Paste renovate.json and review automerge risk, missing minimumReleaseAge, hostRules scope, allowedCommands and safer config patches.",
  "category": "DeveloperApplication",
  "accent": "#087f7b",
  "accent2": "#dc2626",
  "bg": "#f8fffe",
  "panel": "#ffffff",
  "text": "#12201f",
  "soft": "#dcf7f4",
  "shell": "mint",
  "sample": "{\n  \"$schema\": \"https://docs.renovatebot.com/renovate-schema.json\",\n  \"extends\": [\"config:recommended\"],\n  \"automerge\": true,\n  \"automergeType\": \"pr\",\n  \"automergeStrategy\": \"squash\",\n  \"minimumReleaseAge\": \"0\",\n  \"dependencyDashboard\": true,\n  \"dependencyDashboardApproval\": false,\n  \"schedule\": [\"at any time\"],\n  \"hostRules\": [\n    { \"matchHost\": \"github.com\", \"token\": \"$GITHUB_TOKEN\" }\n  ],\n  \"allowedCommands\": [\n    { \"matchCommand\": \"/rebase\" },\n    { \"matchCommand\": \"/merge\" },\n    { \"matchCommand\": \"/approve\" }\n  ],\n  \"packageRules\": [\n    { \"matchUpdateTypes\": [\"major\"], \"automerge\": true },\n    { \"matchDatasources\": [\"docker\"], \"enabled\": false }\n  ]\n}"
};
const SAMPLE = "{\n  \"$schema\": \"https://docs.renovatebot.com/renovate-schema.json\",\n  \"extends\": [\"config:recommended\"],\n  \"automerge\": true,\n  \"automergeType\": \"pr\",\n  \"automergeStrategy\": \"squash\",\n  \"minimumReleaseAge\": \"0\",\n  \"dependencyDashboard\": true,\n  \"dependencyDashboardApproval\": false,\n  \"schedule\": [\"at any time\"],\n  \"hostRules\": [\n    { \"matchHost\": \"github.com\", \"token\": \"$GITHUB_TOKEN\" }\n  ],\n  \"allowedCommands\": [\n    { \"matchCommand\": \"/rebase\" },\n    { \"matchCommand\": \"/merge\" },\n    { \"matchCommand\": \"/approve\" }\n  ],\n  \"packageRules\": [\n    { \"matchUpdateTypes\": [\"major\"], \"automerge\": true },\n    { \"matchDatasources\": [\"docker\"], \"enabled\": false }\n  ]\n}";

type Severity = "pass" | "info" | "warn" | "fail";

type Finding = {
  title: string;
  severity: Severity;
  detail: string;
  target?: string;
  fix?: string;
};

type InventoryRow = {
  key: string;
  label: string;
  status: string;
  score: number;
  meta: string;
};

type Audit = {
  valid: boolean;
  score: number;
  risk: string;
  summary: string;
  metrics: Array<{ label: string; value: string; hint: string }>;
  findings: Finding[];
  inventory: InventoryRow[];
  patch: string;
  command: string;
  parseError?: string;
};

function severityRank(value: Severity) {
  return value === "fail" ? 4 : value === "warn" ? 3 : value === "info" ? 2 : 1;
}

function stripJsonLike(input: string) {
  return input
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/(^|[^:])\/\/.*$/gm, "$1")
    .replace(/,\s*([}\]])/g, "$1");
}

function parseJsonLike(input: string): any {
  return JSON.parse(stripJsonLike(input.trim()));
}

function asArray(value: any): any[] {
  return Array.isArray(value) ? value : value && typeof value === "object" ? Object.entries(value).map(([key, item]) => ({ key, ...(item as object) })) : [];
}

function textIncludes(text: string, words: string[]) {
  const lower = text.toLowerCase();
  return words.some((word) => lower.includes(word));
}

function level(score: number) {
  if (score >= 76) return "High";
  if (score >= 46) return "Medium";
  if (score >= 16) return "Low";
  return "Ready";
}

function buildAudit(findings: Finding[], inventory: InventoryRow[], patch: string, command: string, summary: string, valid = true, parseError?: string): Audit {
  const score = Math.min(100, findings.reduce((sum, finding) => sum + (finding.severity === "fail" ? 18 : finding.severity === "warn" ? 10 : finding.severity === "info" ? 4 : 0), 0));
  const sorted = [...findings].sort((a, b) => severityRank(b.severity) - severityRank(a.severity));
  return {
    valid,
    score,
    risk: level(score),
    summary,
    metrics: [
      { label: "Risk score", value: String(score), hint: level(score) },
      { label: "Critical issues", value: String(findings.filter((f) => f.severity === "fail").length), hint: "fix first" },
      { label: "Warnings", value: String(findings.filter((f) => f.severity === "warn").length), hint: "review" },
      { label: "Items analyzed", value: String(Math.max(1, inventory.length)), hint: "local" },
    ],
    findings: sorted,
    inventory,
    patch,
    command,
    parseError,
  };
}

function analyzeMv3(input: string): Audit {
  try {
    const manifest = parseJsonLike(input);
    const findings: Finding[] = [];
    const permissions = [...asArray(manifest.permissions), ...asArray(manifest.optional_permissions)].map(String);
    const hostPermissions = [...asArray(manifest.host_permissions), ...asArray(manifest.permissions).filter((item) => /\*|https?:\/\//.test(String(item)))].map(String);
    const broadHosts = hostPermissions.filter((pattern) => ["<all_urls>", "*://*/*", "https://*/*", "http://*/*"].includes(pattern));
    if (manifest.manifest_version !== 3) findings.push({ title: "Manifest version is not MV3", severity: "fail", detail: "Chrome Web Store migration work should target manifest_version: 3.", target: "manifest_version", fix: "Set manifest_version to 3 and move background scripts to service_worker." });
    for (const host of broadHosts) findings.push({ title: "Broad host permission", severity: host.includes("http://") ? "fail" : "warn", detail: host + " can trigger scary install/update warnings and wide page access.", target: host, fix: "Replace with exact HTTPS origins or move to optional_host_permissions." });
    const riskyPerms: Record<string, [Severity, string]> = {
      webRequestBlocking: ["fail", "MV3 restricts blocking webRequest and this usually needs enterprise policy or migration."],
      debugger: ["fail", "debugger can inspect and alter browser behavior."],
      nativeMessaging: ["fail", "native messaging expands trust to a local host app."],
      tabs: ["warn", "tabs can expose tab metadata; use activeTab when possible."],
      webRequest: ["warn", "webRequest observes network requests and needs a clear store justification."],
      declarativeNetRequestWithHostAccess: ["warn", "DNR with host access should stay scoped to required sites."],
      downloads: ["warn", "downloads can affect local files and should be optional if user initiated."],
      history: ["fail", "history access creates strong privacy warnings."],
      cookies: ["fail", "cookies access is sensitive and should be scoped tightly."],
      scripting: ["info", "scripting should be paired with narrow host permissions or activeTab."],
    };
    for (const permission of permissions) {
      const rule = riskyPerms[permission];
      if (rule) findings.push({ title: "Permission warning: " + permission, severity: rule[0], detail: rule[1], target: permission, fix: "Document why it is needed or move to optional_permissions if user initiated." });
    }
    if (manifest.background?.scripts || manifest.browser_action || manifest.page_action) findings.push({ title: "MV2 field still present", severity: "fail", detail: "background.scripts, browser_action and page_action are MV2-era patterns.", target: "background/browser_action/page_action", fix: "Use background.service_worker and action." });
    const contentMatches = asArray(manifest.content_scripts).flatMap((script) => asArray(script.matches).map(String));
    for (const match of contentMatches.filter((item) => item === "<all_urls>" || item.includes("*://*/*"))) {
      findings.push({ title: "Content script match is global", severity: "warn", detail: "Global content scripts run on far more pages than most features need.", target: match, fix: "Limit matches or inject via chrome.scripting after a user gesture." });
    }
    const patched = { ...manifest };
    patched.permissions = asArray(manifest.permissions).filter((item) => !["webRequestBlocking", "debugger", "history", "cookies"].includes(String(item)) && !/\*|https?:\/\//.test(String(item)));
    patched.optional_permissions = Array.from(new Set([...(asArray(manifest.optional_permissions).map(String)), "scripting", "tabs"]));
    patched.host_permissions = hostPermissions.length ? ["https://api.example.com/*", "https://*.example.com/*"] : asArray(manifest.host_permissions);
    return buildAudit(
      findings.length ? findings : [{ title: "No high-risk permission pattern detected", severity: "pass", detail: "The manifest has no broad host permissions or known high-warning APIs in this static check." }],
      [
        { key: "permissions", label: String(permissions.length), status: "declared permissions", score: permissions.length, meta: permissions.join(", ") || "none" },
        { key: "hosts", label: String(hostPermissions.length), status: "host permissions", score: broadHosts.length, meta: hostPermissions.join(", ") || "none" },
        { key: "content", label: String(contentMatches.length), status: "content script matches", score: contentMatches.length, meta: contentMatches.join(", ") || "none" },
      ],
      JSON.stringify(patched, null, 2),
      "chrome.management.getPermissionWarningsByManifest(JSON.stringify(manifest), console.table)",
      "Manifest parsed locally. Findings approximate Chrome permission-warning risk and least-privilege migration work.",
    );
  } catch (error) {
    return buildAudit([{ title: "Manifest JSON could not be parsed", severity: "fail", detail: String(error), fix: "Paste a valid manifest.json object." }], [], "{}", "npx wxt prepare", "Parsing failed.", false, String(error));
  }
}

function analyzeFlag(input: string): Audit {
  try {
    const config = parseJsonLike(input);
    const flags = asArray(config.flags);
    const now = new Date("2026-06-15T00:00:00Z");
    const findings: Finding[] = [];
    const inventory: InventoryRow[] = [];
    for (const flag of flags) {
      const key = String(flag.key || flag.name || "unnamed-flag");
      let score = 0;
      const owner = String(flag.owner || flag.maintainer || "");
      const expiryRaw = flag.expiresAt || flag.expiry || flag.expires || flag.ttl;
      const expiry = expiryRaw ? new Date(String(expiryRaw)) : null;
      const targetText = JSON.stringify(flag.targeting || flag.rules || {});
      const rollout = Number(flag.rollout ?? flag.percentage ?? flag.targeting?.percentage ?? flag.targeting?.rules?.[0]?.percentage ?? 0);
      if (!owner.trim()) {
        score += 25;
        findings.push({ title: "Missing flag owner", severity: "fail", detail: key + " has no accountable owner.", target: key, fix: "Add owner/team metadata and route cleanup to that team." });
      }
      if (!expiryRaw) {
        score += 18;
        findings.push({ title: "No expiry date", severity: "warn", detail: key + " can become permanent debt without a review date.", target: key, fix: "Add expiresAt and a cleanup ticket." });
      } else if (expiry && expiry < now) {
        score += 32;
        findings.push({ title: "Expired feature flag", severity: "fail", detail: key + " expired on " + String(expiryRaw) + ".", target: key, fix: "Remove code paths or mark as permanent only with owner approval." });
      }
      if (rollout >= 100 || String(flag.defaultVariant || "").toLowerCase().includes("treatment")) {
        score += 15;
        findings.push({ title: "Likely completed rollout", severity: "warn", detail: key + " appears fully rolled out or defaults to treatment.", target: key, fix: "Schedule cleanup after verifying no rollback need." });
      }
      if (/email|phone|ip|ssn|address/i.test(targetText)) {
        score += 18;
        findings.push({ title: "Sensitive targeting key", severity: "warn", detail: key + " targets on potentially personal attributes.", target: key, fix: "Prefer coarse cohort IDs or documented consent boundaries." });
      }
      inventory.push({
        key,
        label: String(flag.name || key),
        status: expiry && expiry < now ? "expired" : rollout >= 100 ? "complete rollout" : "active",
        score,
        meta: owner ? "owner: " + owner : "owner missing",
      });
    }
    const cleanup = inventory.filter((row) => row.score >= 30).map((row) => "- remove or review " + row.key + " (" + row.status + ")").join("\n");
    return buildAudit(
      findings.length ? findings : [{ title: "Flag inventory looks governed", severity: "pass", detail: "No missing owners, expired dates or obvious sensitive targeting keys were found." }],
      inventory,
      cleanup || "- keep current flags; add automated expiry checks in CI",
      "flagd validate --uri file:./flags.json && openfeature-operator reconcile",
      "Flag file parsed locally. Debt score combines ownership, expiry, rollout completion and targeting blast radius.",
    );
  } catch (error) {
    return buildAudit([{ title: "Flag JSON could not be parsed", severity: "fail", detail: String(error), fix: "Paste flagd or OpenFeature-style JSON." }], [], "{}", "flagd validate --uri file:./flags.json", "Parsing failed.", false, String(error));
  }
}

function analyzeRenovate(input: string): Audit {
  try {
    const config = parseJsonLike(input);
    const findings: Finding[] = [];
    const inventory: InventoryRow[] = [];
    if (config.automerge === true) findings.push({ title: "Global automerge enabled", severity: "fail", detail: "Global automerge can merge major or ecosystem-risky updates unless tightly scoped.", target: "automerge", fix: "Disable global automerge and scope low-risk packageRules only." });
    if (!config.minimumReleaseAge || String(config.minimumReleaseAge).startsWith("0")) findings.push({ title: "No minimumReleaseAge buffer", severity: "warn", detail: "New package releases can merge before malicious or broken versions are pulled.", target: "minimumReleaseAge", fix: "Use at least 3 days; 14 days is safer for broad third-party automerge." });
    if (config.dependencyDashboardApproval === false) findings.push({ title: "Dependency Dashboard approval disabled", severity: "warn", detail: "Riskier updates can bypass manual dashboard approval.", target: "dependencyDashboardApproval", fix: "Enable approval for major, Docker and security-sensitive ecosystems." });
    if (JSON.stringify(config.schedule || "").toLowerCase().includes("any time")) findings.push({ title: "Unbounded update schedule", severity: "info", detail: "Updates may land during incident windows or release freezes.", target: "schedule", fix: "Restrict to weekday maintenance windows." });
    const hostRules = asArray(config.hostRules);
    hostRules.forEach((rule, index) => {
      if (rule.token && rule.readOnly !== true) findings.push({ title: "hostRules token is not readOnly", severity: "warn", detail: "A Renovate host token should be read-only unless writes are explicitly required.", target: "hostRules[" + index + "]", fix: "Set readOnly: true or use a least-privilege token." });
      if (rule.insecureRegistry === true) findings.push({ title: "Insecure registry allowed", severity: "fail", detail: "insecureRegistry permits unsafe dependency source transport.", target: "hostRules[" + index + "]", fix: "Remove insecureRegistry or pin to a trusted internal network." });
    });
    const commands = asArray(config.allowedCommands);
    if (commands.length) findings.push({ title: "allowedCommands configured", severity: "warn", detail: "Commands broaden what Renovate can execute from PR comments.", target: "allowedCommands", fix: "Keep a minimal allowlist and avoid merge/approve commands." });
    const packageRules = asArray(config.packageRules);
    packageRules.forEach((rule, index) => {
      if (rule.automerge === true && JSON.stringify(rule.matchUpdateTypes || "").includes("major")) findings.push({ title: "Major updates automerge", severity: "fail", detail: "Major update automerge should be exceptional and heavily tested.", target: "packageRules[" + index + "]", fix: "Disable automerge for major updates." });
      if (rule.enabled === false && JSON.stringify(rule.matchDatasources || "").includes("docker")) findings.push({ title: "Docker updates disabled", severity: "warn", detail: "Disabled container updates can hide base image CVEs.", target: "packageRules[" + index + "]", fix: "Use schedules or grouping instead of disabling the datasource." });
    });
    inventory.push(
      { key: "automerge", label: config.automerge ? "enabled" : "disabled", status: "merge policy", score: config.automerge ? 80 : 5, meta: String(config.automerge) },
      { key: "minimumReleaseAge", label: String(config.minimumReleaseAge || "missing"), status: "release-age buffer", score: config.minimumReleaseAge ? 15 : 60, meta: "supply-chain delay" },
      { key: "hostRules", label: String(hostRules.length), status: "host credentials", score: hostRules.length * 20, meta: "tokens and registries" },
      { key: "packageRules", label: String(packageRules.length), status: "rule precedence", score: packageRules.length * 8, meta: "review conflicts" },
    );
    const safer = { ...config, automerge: false, minimumReleaseAge: config.minimumReleaseAge && !String(config.minimumReleaseAge).startsWith("0") ? config.minimumReleaseAge : "3 days", dependencyDashboard: true, dependencyDashboardApproval: true };
    safer.hostRules = hostRules.map((rule) => ({ ...rule, readOnly: true, insecureRegistry: undefined }));
    safer.allowedCommands = commands.filter((rule) => !/merge|approve/i.test(JSON.stringify(rule)));
    return buildAudit(
      findings.length ? findings : [{ title: "Renovate policy looks conservative", severity: "pass", detail: "No broad automerge, missing release-age buffer or high-risk hostRules were detected." }],
      inventory,
      JSON.stringify(safer, null, 2),
      "npx --yes --package renovate -- renovate-config-validator --strict --no-global renovate.json",
      "Config parsed locally. Risk findings complement Renovate's schema validator with policy-review checks.",
    );
  } catch (error) {
    return buildAudit([{ title: "Renovate config could not be parsed", severity: "fail", detail: String(error), fix: "Paste renovate.json or JSON5-like config." }], [], "{}", "npx --yes --package renovate -- renovate-config-validator --strict", "Parsing failed.", false, String(error));
  }
}

function analyzeFlake(input: string): Audit {
  const findings: Finding[] = [];
  const lines = input.split(/\r?\n/);
  const locatorMatch = input.match(/getBy(Role|Text|Label|TestId|Placeholder)\([^\n]+/i)?.[0] || input.match(/locator\([^\n]+/i)?.[0];
  const timeoutMatch = input.match(/timeout(?: after)?\s*:?\s*(\d+)ms/i);
  const retry = /retry|passed on retry|retry #|retries/i.test(input);
  if (/toBeVisible|actionability|element is not visible|disabled|aria-busy/i.test(input)) findings.push({ title: "Actionability timing failure", severity: "fail", detail: "The failure waits for visibility/enabled state while the page is still changing.", target: locatorMatch || "locator", fix: "Assert toBeEnabled or wait for the specific async operation before clicking." });
  if (/getByRole|locator|strict mode|resolved to/i.test(input)) findings.push({ title: "Locator stability needs review", severity: "warn", detail: "Role or CSS locator evidence appears in the failure log.", target: locatorMatch || "locator", fix: "Prefer resilient role/name or test id locators and avoid text that changes with state." });
  if (/waitForResponse|network|ECONN|fetch|XHR|request failed/i.test(input)) findings.push({ title: "Network wait contributes to flake", severity: "warn", detail: "Network or response waits appear close to the failing action.", target: "network", fix: "Wait for the exact response predicate and assert UI readiness after response handling." });
  if (timeoutMatch) findings.push({ title: "Timeout is too tight for CI evidence", severity: "info", detail: "Detected timeout " + timeoutMatch[1] + "ms.", target: "timeout", fix: "Raise expect timeout only after fixing readiness and locator quality." });
  if (retry) findings.push({ title: "Retry-only pass pattern", severity: "warn", detail: "Retries indicate the same commit can pass after a transient failure.", target: "retry", fix: "Record trace on first retry and compare failed/pass attempts." });
  if (!/trace\.zip|trace/i.test(input)) findings.push({ title: "Trace artifact missing", severity: "warn", detail: "A failed Playwright run without trace slows root-cause triage.", target: "trace", fix: "Set use.trace to on-first-retry in CI." });
  const actions = lines.filter((line) => /page\.|expect\(|locator|getBy/i.test(line)).slice(0, 8);
  const inventory = actions.map((action, index) => ({
    key: "step-" + (index + 1),
    label: action.trim().slice(0, 80),
    status: index === actions.length - 1 ? "failure edge" : "action",
    score: index === actions.length - 1 ? 70 : 15,
    meta: action.includes("wait") ? "wait" : action.includes("expect") ? "assertion" : "interaction",
  }));
  const patch = `import { defineConfig } from "@playwright/test";

export default defineConfig({
  retries: process.env.CI ? 2 : 0,
  timeout: 30_000,
  expect: { timeout: 7_000 },
  use: {
    trace: "on-first-retry",
    video: "retain-on-failure",
    screenshot: "only-on-failure",
    actionTimeout: 10_000,
    navigationTimeout: 30_000
  }
});`;
  return buildAudit(
    findings.length ? findings : [{ title: "No obvious flake smell found", severity: "pass", detail: "The pasted log did not include common Playwright actionability, timeout, locator or retry patterns." }],
    inventory,
    patch,
    "npx playwright show-trace test-results/<failed-test>/trace.zip",
    "Failure log parsed locally. Findings classify evidence patterns and suggest what to inspect in the official trace viewer.",
  );
}

function normalizeVulns(doc: any) {
  if (Array.isArray(doc.vulnerabilities)) return doc.vulnerabilities;
  if (Array.isArray(doc.results)) return doc.results.flatMap((result: any) => asArray(result.packages).flatMap((pkg: any) => asArray(pkg.vulnerabilities).map((vuln: any) => ({ ...vuln, affects: [{ ref: pkg.package?.purl || pkg.package?.name || result.source?.path }] }))));
  return [];
}

function analyzeVex(input: string): Audit {
  try {
    const doc = parseJsonLike(input);
    const components = asArray(doc.components);
    const vulns = normalizeVulns(doc);
    const findings: Finding[] = [];
    const inventory: InventoryRow[] = [];
    const byRef = new Map<string, any>();
    components.forEach((component) => {
      if (component["bom-ref"]) byRef.set(String(component["bom-ref"]), component);
      if (component.purl) byRef.set(String(component.purl), component);
      if (component.name) byRef.set(String(component.name), component);
    });
    const counts: Record<string, number> = {};
    for (const vuln of vulns) {
      const id = String(vuln.id || vuln.osv?.id || vuln.cve || "unknown-vulnerability");
      const status = String(vuln.analysis?.state || vuln.analysis?.status || vuln.status || "missing_evidence").toLowerCase();
      counts[status] = (counts[status] || 0) + 1;
      const ref = String(vuln.affects?.[0]?.ref || vuln.package?.purl || vuln.package?.name || "");
      const component = byRef.get(ref);
      const severity = String(vuln.ratings?.[0]?.severity || vuln.database_specific?.severity || vuln.severity?.[0]?.type || "unknown");
      const missingJustification = !vuln.analysis?.justification && status !== "affected" && status !== "fixed";
      const missingAction = !vuln.analysis?.detail && !vuln.analysis?.response && !vuln.analysis?.actionStatement;
      if (status === "affected") findings.push({ title: "Affected vulnerability", severity: /critical|high/i.test(severity) ? "fail" : "warn", detail: id + " is marked affected for " + (component?.name || ref || "a component") + ".", target: id, fix: "Add remediation owner, target version and action statement." });
      if (status === "under_investigation") findings.push({ title: "Under investigation without closure", severity: "warn", detail: id + " still needs a dated decision.", target: id, fix: "Add evidence, review date and current mitigation." });
      if (missingJustification) findings.push({ title: "Missing VEX justification", severity: "warn", detail: id + " needs a machine-readable justification for its status.", target: id, fix: "Use CycloneDX analysis.justification such as code_not_reachable when defensible." });
      if (missingAction) findings.push({ title: "Missing action statement", severity: "info", detail: id + " lacks detail/response/actionStatement evidence.", target: id, fix: "State upgrade, mitigation, accepted risk or no_action rationale." });
      inventory.push({ key: id, label: component?.name || ref || "unmapped component", status, score: status === "affected" ? 85 : status === "under_investigation" ? 60 : missingJustification ? 42 : 12, meta: severity });
    }
    const actionNote = inventory
      .slice(0, 8)
      .map((row) => row.key + ": " + row.status + " - " + row.label + " (" + row.meta + ")")
      .join("\n");
    return buildAudit(
      findings.length ? findings : [{ title: "VEX evidence is complete for visible vulnerabilities", severity: "pass", detail: "No missing justification or affected high-priority item was detected." }],
      inventory,
      actionNote || "No vulnerabilities found in pasted document.",
      "osv-scanner --sbom=sbom.json --format=cyclonedx --output=osv-results.json",
      "SBOM/VEX JSON parsed locally. Status grouping is based on CycloneDX analysis state or OSV vulnerability entries.",
    );
  } catch (error) {
    return buildAudit([{ title: "SBOM/VEX JSON could not be parsed", severity: "fail", detail: String(error), fix: "Paste CycloneDX, VEX or OSV JSON." }], [], "{}", "osv-scanner --sbom=sbom.json", "Parsing failed.", false, String(error));
  }
}

function analyze(kind: string, input: string): Audit {
  if (kind === "mv3") return analyzeMv3(input);
  if (kind === "flag") return analyzeFlag(input);
  if (kind === "renovate") return analyzeRenovate(input);
  if (kind === "flake") return analyzeFlake(input);
  return analyzeVex(input);
}

function copyText(text: string) {
  void navigator.clipboard?.writeText(text);
}

function IconBadge() {
  if (SITE.kind === "flag") return <Flag size={24} />;
  if (SITE.kind === "renovate") return <Shield size={24} />;
  if (SITE.kind === "flake") return <Bug size={24} />;
  if (SITE.kind === "vex") return <Package size={24} />;
  return <Shield size={24} />;
}

function MetricStrip({ audit }: { audit: Audit }) {
  return (
    <section className="metric-strip" aria-label="Audit metrics">
      {audit.metrics.map((metric) => (
        <div className="metric" key={metric.label}>
          <span>{metric.label}</span>
          <strong>{metric.value}</strong>
          <em>{metric.hint}</em>
        </div>
      ))}
    </section>
  );
}

function FindingList({ audit }: { audit: Audit }) {
  return (
    <div className="findings">
      {audit.findings.map((finding, index) => (
        <article className={"finding " + finding.severity} key={finding.title + index}>
          <div>
            {finding.severity === "pass" ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
            <strong>{finding.title}</strong>
          </div>
          <p>{finding.detail}</p>
          {finding.target ? <code>{finding.target}</code> : null}
          {finding.fix ? <small>{finding.fix}</small> : null}
        </article>
      ))}
    </div>
  );
}

function InputPanel({ input, setInput, title }: { input: string; setInput: (value: string) => void; title: string }) {
  return (
    <section className="input-panel">
      <div className="panel-head">
        <h2>{title}</h2>
        <div className="head-actions">
          <button type="button" onClick={() => setInput(SAMPLE)}><Play size={16} />Sample</button>
          <button type="button" onClick={() => setInput("")}>Clear</button>
        </div>
      </div>
      <textarea aria-label={title} spellCheck={false} value={input} onChange={(event) => setInput(event.target.value)} />
      <div className="status-row">
        <span>{input.split(/\r?\n/).length} lines</span>
        <span>{input.length.toLocaleString()} chars</span>
      </div>
    </section>
  );
}

function PatchPanel({ audit, label = "Recommended patch" }: { audit: Audit; label?: string }) {
  return (
    <section className="patch-panel">
      <div className="panel-head">
        <h2>{label}</h2>
        <button type="button" onClick={() => copyText(audit.patch)}><Copy size={16} />Copy</button>
      </div>
      <pre>{audit.patch}</pre>
      <div className="command">
        <Terminal size={16} />
        <code>{audit.command}</code>
        <button type="button" onClick={() => copyText(audit.command)} aria-label="Copy command"><Copy size={14} /></button>
      </div>
    </section>
  );
}

function InventoryTable({ audit }: { audit: Audit }) {
  return (
    <section className="inventory">
      <div className="panel-head"><h2>Inventory</h2><span>{audit.inventory.length} rows</span></div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Item</th><th>Status</th><th>Score</th><th>Context</th></tr></thead>
          <tbody>
            {audit.inventory.map((row) => (
              <tr key={row.key}>
                <td><strong>{row.label}</strong><code>{row.key}</code></td>
                <td>{row.status}</td>
                <td><span className={row.score >= 70 ? "hot" : row.score >= 35 ? "warm" : "cool"}>{row.score}</span></td>
                <td>{row.meta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Mv3Layout({ audit, input, setInput }: { audit: Audit; input: string; setInput: (value: string) => void }) {
  return (
    <>
      <MetricStrip audit={audit} />
      <section className="mv3-grid">
        <InputPanel input={input} setInput={setInput} title="manifest.json" />
        <section className="warning-board">
          <div className="panel-head"><h2>Permission warnings</h2><Search size={18} /></div>
          <FindingList audit={audit} />
        </section>
        <PatchPanel audit={audit} label="Least privilege patch" />
      </section>
    </>
  );
}

function FlagLayout({ audit, input, setInput }: { audit: Audit; input: string; setInput: (value: string) => void }) {
  return (
    <>
      <MetricStrip audit={audit} />
      <section className="flag-grid">
        <InputPanel input={input} setInput={setInput} title="flagd / OpenFeature JSON" />
        <InventoryTable audit={audit} />
        <aside className="side-stack">
          <section className="risk-dial"><Gauge size={34} /><strong>{audit.score}</strong><span>{audit.risk} debt</span></section>
          <FindingList audit={audit} />
        </aside>
        <PatchPanel audit={audit} label="Cleanup backlog" />
      </section>
    </>
  );
}

function RenovateLayout({ audit, input, setInput }: { audit: Audit; input: string; setInput: (value: string) => void }) {
  return (
    <section className="renovate-shell">
      <nav className="rail"><Shield size={22} /><span>Scout</span><ListChecks size={22} /><Wrench size={22} /><GitBranch size={22} /></nav>
      <div className="renovate-main">
        <MetricStrip audit={audit} />
        <div className="renovate-grid">
          <InputPanel input={input} setInput={setInput} title="renovate.json" />
          <section className="lanes">
            <div className="panel-head"><h2>Dependency automation risk lanes</h2><RefreshCw size={18} /></div>
            <FindingList audit={audit} />
            <InventoryTable audit={audit} />
          </section>
          <PatchPanel audit={audit} label="Safer config patch" />
        </div>
      </div>
    </section>
  );
}

function FlakeLayout({ audit, input, setInput }: { audit: Audit; input: string; setInput: (value: string) => void }) {
  return (
    <>
      <MetricStrip audit={audit} />
      <section className="flake-grid">
        <InputPanel input={input} setInput={setInput} title="Failure log or JSON report" />
        <section className="timeline">
          <div className="panel-head"><h2>Trace action timeline</h2><Clock size={18} /></div>
          {audit.inventory.map((row, index) => (
            <div className="timeline-row" key={row.key}>
              <span>{index + 1}</span>
              <div><strong>{row.label}</strong><em>{row.meta}</em></div>
              <b className={row.score >= 70 ? "hot" : "cool"}>{row.status}</b>
            </div>
          ))}
          <PatchPanel audit={audit} label="Recommended Playwright config" />
        </section>
        <aside className="side-stack">
          <section className="risk-dial"><Gauge size={34} /><strong>{Math.min(99, audit.score + 20)}%</strong><span>flake confidence</span></section>
          <FindingList audit={audit} />
        </aside>
      </section>
    </>
  );
}

function VexLayout({ audit, input, setInput }: { audit: Audit; input: string; setInput: (value: string) => void }) {
  const lanes = ["affected", "not_affected", "fixed", "under_investigation", "missing_evidence"];
  return (
    <>
      <MetricStrip audit={audit} />
      <section className="vex-grid">
        <InputPanel input={input} setInput={setInput} title="CycloneDX / VEX / OSV JSON" />
        <section className="status-lanes">
          <div className="panel-head"><h2>Exploitability status</h2><FileWarning size={18} /></div>
          <div className="lane-grid">
            {lanes.map((lane) => (
              <article className="status-lane" key={lane}>
                <h3>{lane.replace("_", " ")}</h3>
                {audit.inventory.filter((row) => row.status === lane).slice(0, 5).map((row) => (
                  <div className="vuln-card" key={row.key}><strong>{row.key}</strong><span>{row.label}</span><em>{row.meta}</em></div>
                ))}
              </article>
            ))}
          </div>
        </section>
        <aside className="side-stack">
          <section className="risk-dial"><Gauge size={34} /><strong>{audit.score}</strong><span>{audit.risk} priority</span></section>
          <FindingList audit={audit} />
        </aside>
        <PatchPanel audit={audit} label="Action statement draft" />
      </section>
    </>
  );
}

function App() {
  const [input, setInput] = useState(SAMPLE);
  const audit = useMemo(() => analyze(SITE.kind, input), [input]);
  const Layout = SITE.kind === "mv3" ? Mv3Layout : SITE.kind === "flag" ? FlagLayout : SITE.kind === "renovate" ? RenovateLayout : SITE.kind === "flake" ? FlakeLayout : VexLayout;

  return (
    <main className={"app app-" + SITE.kind}>
      <header className="topbar">
        <div className="brand">
          <div className="brand-icon"><IconBadge /></div>
          <div>
            <h1>{SITE.name}</h1>
            <p>{SITE.description}</p>
          </div>
        </div>
        <div className="top-actions">
          <span className={"risk-pill " + audit.risk.toLowerCase()}>{audit.risk}</span>
          <button type="button" onClick={() => setInput(SAMPLE)}><Upload size={16} />Load sample</button>
          <button type="button" className="primary" onClick={() => copyText(JSON.stringify(audit.findings, null, 2))}><Copy size={16} />Copy findings</button>
        </div>
      </header>
      <section className="summary-line">
        <div><Gauge size={18} /><strong>{audit.risk} risk</strong><span>{audit.summary}</span></div>
        <div><CheckCircle2 size={18} /><span>{audit.valid ? "Input parsed locally" : audit.parseError}</span></div>
      </section>
      <Layout audit={audit} input={input} setInput={setInput} />
      <footer>
        <a data-google-vignette="false" href="/about.html">About</a>
        <a data-google-vignette="false" href="/privacy.html">Privacy</a>
        <span>Local browser analysis. Verify with official tools before shipping.</span>
      </footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
